import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { apiService } from '../../../../services/api';
import DocumentosOportunidade from '../DocumentosOportunidade';
import { Anotacao, Arquivo, AnotacaoAPI } from '../../../../types/notes';
import ImageWithBasePath from '../../../../core/common/imageWithBasePath';

interface DocumentosOportunidadeTabsProps {
    oportunidadeId: number;
}

const DocumentosOportunidadeTabs: React.FC<DocumentosOportunidadeTabsProps> = ({
    oportunidadeId
}) => {
    const [activeTab, setActiveTab] = useState('documentos');
    const [anotacoes, setAnotacoes] = useState<Anotacao[]>([]);
    const [loadingAnotacoes, setLoadingAnotacoes] = useState(false);

    // Função auxiliar para formatar o tamanho do arquivo
    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Função para determinar o ícone baseado no tipo do arquivo
    const getFileIcon = (tipo: string): string => {
        if (tipo.includes('image')) return 'photo';
        if (tipo.includes('pdf')) return 'file-text';
        if (tipo.includes('spreadsheet') || tipo.includes('excel')) return 'table';
        if (tipo.includes('word') || tipo.includes('document')) return 'file-text';
        if (tipo.includes('zip') || tipo.includes('rar')) return 'archive';
        return 'file';
    };

    const fetchAnotacoes = async () => {
        if (!oportunidadeId) return;

        try {
            setLoadingAnotacoes(true);
            const response = await apiService.getAnotacoes({
                id_oportunidade: oportunidadeId,
                page: 1,
                per_page: 10
            });

            if (response.success && Array.isArray(response.data)) {
                // Primeiro convertemos para unknown e depois para o tipo desejado
                const anotacoesData = response.data as unknown as AnotacaoAPI[];
                const anotacoesProcessadas: Anotacao[] = anotacoesData.map(note => ({
                    ...note,
                    arquivos: note.arquivos ? JSON.parse(note.arquivos) as Arquivo[] : []
                }));
                setAnotacoes(anotacoesProcessadas);
            }
        } catch (error) {
            console.error('Erro ao carregar anotações:', error);
            toast.error('Erro ao carregar anotações');
        } finally {
            setLoadingAnotacoes(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'anotacoes') {
            fetchAnotacoes();
        }
    }, [activeTab, oportunidadeId]);

    return (
        <div className="card">
            <div className="card-header pb-0">
                <ul className="nav nav-tabs nav-tabs-bordered">
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === 'documentos' ? 'active' : ''}`}
                            onClick={() => setActiveTab('documentos')}
                        >
                            <i className="ti ti-file me-1"></i>
                            Documentos
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === 'anotacoes' ? 'active' : ''}`}
                            onClick={() => setActiveTab('anotacoes')}
                        >
                            <i className="ti ti-notes me-1"></i>
                            Anotações
                        </button>
                    </li>
                </ul>
            </div>
            <div className="card-body">
                <div className="tab-content">
                    {/* Aba de Documentos */}
                    <div className={`tab-pane fade ${activeTab === 'documentos' ? 'show active' : ''}`}>
                        <DocumentosOportunidade oportunidadeId={oportunidadeId} />
                    </div>

                    {/* Aba de Anotações */}
                    <div className={`tab-pane fade ${activeTab === 'anotacoes' ? 'show active' : ''}`}>
                        <div className="card">
                            <div className="card-header d-flex justify-content-between">
                                <h4 className="fw-semibold">Anotações da Oportunidade</h4>
                            </div>
                            <div className="card-body">
                                {loadingAnotacoes ? (
                                    <div className="text-center p-3">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Carregando...</span>
                                        </div>
                                    </div>
                                ) : anotacoes.length > 0 ? (
                                    <div className="notes-activity">
                                        {anotacoes.map((note) => (
                                            <div className="card mb-3" key={note.id}>
                                                <div className="card-body">
                                                    <div className="d-flex align-items-center justify-content-between pb-2">
                                                        <div className="d-inline-flex align-items-center mb-2">
                                                            <span className="avatar avatar-sm avatar-rounded avatar-gray me-2 flex-shrink-0">
                                                                <ImageWithBasePath
                                                                    src={`https://ui-avatars.com/api/?name=${encodeURIComponent(note.usuario_nome)}&background=0D8ABC&color=fff`}
                                                                    alt={note.usuario_nome}
                                                                />
                                                            </span>
                                                            <div>
                                                                <h6 className="fw-medium mb-1">
                                                                    {note.usuario_nome}
                                                                </h6>
                                                                <p className="mb-0">
                                                                    {new Date(note.data_criacao).toLocaleDateString('pt-BR', {
                                                                        day: '2-digit',
                                                                        month: 'short',
                                                                        year: 'numeric',
                                                                        hour: '2-digit',
                                                                        minute: '2-digit'
                                                                    })}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <h5 className="fw-medium mb-1">{note.titulo}</h5>
                                                    <p style={{ whiteSpace: 'pre-line' }}>{note.observacoes}</p>

                                                    {note.arquivos && note.arquivos.length > 0 && (
                                                        <div className="d-inline-flex align-items-center flex-wrap">
                                                            {note.arquivos.map((arquivo, index) => (
                                                                <div className="note-download me-3" key={index}>
                                                                    <div className="note-info">
                                                                        <span className="note-icon bg-secondary-success">
                                                                            <i className={`ti ti-${getFileIcon(arquivo.tipo)}`} />
                                                                        </span>
                                                                        <div>
                                                                            <h6 className="fw-medium mb-1">
                                                                                {arquivo.nome_original}
                                                                            </h6>
                                                                            <p>{formatFileSize(arquivo.tamanho)}</p>
                                                                        </div>
                                                                    </div>
                                                                    <Link
                                                                        to="#"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            apiService.downloadArquivoAnotacao(
                                                                                note.id,
                                                                                arquivo.nome_salvo
                                                                            );
                                                                        }}
                                                                    >
                                                                        <i className="ti ti-arrow-down" />
                                                                    </Link>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p>Sem anotações registradas para esta oportunidade</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DocumentosOportunidadeTabs;